import { useCallback, useMemo, useState } from 'react';
import { freeId } from '../domain/freeId';
import { myselfRecipient } from '../domain/notifications';
import {
  invoiceEmailChoices, invoiceEmailFacts, invoiceEmailFigures, invoiceEmailFilled, invoiceEmailInsertPlaceholder,
  invoiceEmailWrite,
} from '../domain/invoiceEmail';
import useMessageBodyEdits from './useMessageBodyEdits';

// An invoice row's Email (#622): MMS's Send Invoice drawer — From, one To,
// "BCC me on this email", the Invoice and Invoice Reminder tabs, an editable
// Subject and Message — and the send, which queues one `invoice` message on
// the outbox like every other message; the sender attaches the invoice's PDF.
// What MMS does and where ours differs is core/src/invoice_email.rs; the
// state is here.
//
// The drawer opens filled: MMS's subject reads `Nguyen 8/29/2026 Invoice`,
// not its tokens, so each tab's template is filled for the contact on To
// (`invoiceEmailFilled`), with the invoice's date and Total Due as its email
// writes them (`invoiceEmailFigures`: the frozen body's, where the invoice has
// one), and the teacher edits that. Switching tab loads that tab's template,
// as MMS's tabs do. Picking another contact on To fills the template again for
// them unless the teacher has edited the words, which a pick must not throw
// away. That last rule is ours: MMS's To is unchecked with more than one
// contact on the family.
//
// One Send is one email: the message id comes from a fresh id made when the
// drawer opens (`freeId`), so a double press writes one document; opening the
// drawer again is a new email.
//
// `myEmail` is the teacher's sign-in address: BCC me's "Myself" (on `cc`,
// which the sender sends as a message of its own — every recipient is BCC'ed
// in effect, functions/delivery/transport/sendgrid.js). `familyFor`,
// `contactsFor`, `billingContactFor` and `studentIdsFor` are the page's
// lookups by family id.

const CLOSED = {
  active: false, seed: null, invoice: null, template: 'invoice', choices: [], to: null, from: null,
  bccMe: false, subject: '', body: '', edited: false, error: null, notice: null, sending: false,
};

export default function useSendInvoice({
  send = null, myEmail = null, fromChoices = [], studioName = null, teacherName = null, studioLocale = null,
  familyFor = () => null, contactsFor = () => [], billingContactFor = () => null, studentIdsFor = () => [],
} = {}) {
  const [state, setState] = useState(CLOSED);
  const firstFrom = fromChoices[0]?.key ?? null;

  // What the values are made of, for the contact at `index` of `choices`.
  const factsFor = useCallback((invoice, choices, index) => {
    const family = invoice?.familyId ? familyFor(invoice.familyId) : null;
    return invoiceEmailFacts({
      contact: choices[index]?.contact || null,
      familyLastName: family?.lastName || null,
      ...invoiceEmailFigures(invoice, studioLocale),
      studioName,
      teacherName,
    });
  }, [familyFor, studioLocale, studioName, teacherName]);

  const open = useCallback((invoice) => {
    if (!invoice) return;
    const familyId = invoice.familyId || null;
    const family = familyId ? familyFor(familyId) : null;
    const { choices, at } = invoiceEmailChoices(
      family ? { ...family, id: family.id || familyId } : null,
      familyId ? contactsFor(familyId) : [],
      familyId ? billingContactFor(familyId) : null,
    );
    const filled = invoiceEmailFilled('invoice', factsFor(invoice, choices, at ?? 0));
    setState({
      ...CLOSED,
      active: true,
      seed: freeId('msg'),
      invoice,
      choices,
      to: at,
      from: firstFrom,
      subject: filled.subject,
      body: filled.body,
    });
  }, [familyFor, contactsFor, billingContactFor, factsFor, firstFrom]);

  const close = useCallback(() => setState(CLOSED), []);

  // A tab: its template, filled for whoever is on To.
  const pickTemplate = useCallback((template) => setState((s) => {
    const filled = invoiceEmailFilled(template, factsFor(s.invoice, s.choices, s.to ?? 0));
    return {
      ...s, template, subject: filled.subject, body: filled.body, edited: false, error: null, notice: null,
    };
  }), [factsFor]);

  // To: another contact — and the template filled for them, unless edited.
  const pickTo = useCallback((index) => setState((s) => {
    if (s.edited) return { ...s, to: index, error: null };
    const filled = invoiceEmailFilled(s.template, factsFor(s.invoice, s.choices, index));
    return {
      ...s, to: index, subject: filled.subject, body: filled.body, error: null,
    };
  }), [factsFor]);

  const change = useCallback((patch) => setState((s) => ({
    ...s,
    ...patch,
    edited: s.edited || 'subject' in patch || 'body' in patch,
    error: null,
    notice: null,
  })), []);

  // The placeholder menu (the invoice's tokens) and the toolbar, over the
  // box's selection (useMessageBodyEdits.js).
  const applyEdit = useCallback((patch) => setState((s) => (
    'body' in patch ? {
      ...s, body: patch.body, edited: true, error: null, notice: null,
    } : { ...s, notice: patch.notice }
  )), []);
  const { format, insertImage, insertPlaceholder } = useMessageBodyEdits(state.body, applyEdit, {
    insert: invoiceEmailInsertPlaceholder,
  });

  const myself = useMemo(() => myselfRecipient(myEmail), [myEmail]);

  // The values the Preview fills a token with: the recipient on To's.
  const facts = useMemo(
    () => (state.active ? factsFor(state.invoice, state.choices, state.to ?? 0) : null),
    [state.active, state.invoice, state.choices, state.to, factsFor],
  );

  const submit = useCallback(async () => {
    const {
      active, seed, invoice, choices, to, from, bccMe, subject, body, template,
    } = state;
    if (!active || !send) return false;
    const recipient = to != null ? choices[to]?.recipient : null;
    const write = invoiceEmailWrite({
      seed,
      template,
      to: recipient ? [recipient] : [],
      cc: bccMe && myself ? [myself] : [],
      subject,
      body,
      invoiceId: invoice?.id || null,
      familyId: invoice?.familyId || null,
      studentIds: invoice?.familyId ? studentIdsFor(invoice.familyId) : [],
      sendAs: from,
    });
    if (write.refusal) {
      setState((s) => ({ ...s, error: write.refusal }));
      return false;
    }
    setState((s) => ({ ...s, sending: true, error: null }));
    return send(write).then(
      () => {
        setState(CLOSED);
        return true;
      },
      (err) => {
        // Refused outright (the rules, a lapsed studio); a write merely owed
        // settles as sent and is the queue's to deliver.
        setState((s) => ({ ...s, sending: false, error: err?.message || String(err) }));
        return false;
      },
    );
  }, [state, send, myself, studentIdsFor]);

  return {
    ...state,
    isOpen: state.active,
    open,
    close,
    change,
    pickTemplate,
    pickTo,
    format,
    insertImage,
    insertPlaceholder,
    submit,
    facts,
    canBccMe: Boolean(myself),
    fromChoices,
  };
}
